import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loader } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Save, ArrowUpRight } from "lucide-react";

const DEPARTMENTS = ["IT", "Electrical", "HVAC", "Housekeeping", "Plumbing", "Security", "Furniture", "General"];
const ROLE_LABEL = { SUPPORT_TEAM: "Support Team", SERVICE_HEAD: "Service Head", IT_ADMIN: "IT Admin", COMPANY_ADMIN: "Company Admin" };

export default function TicketConfig() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const roles = cfg?.escalation_roles || ["SUPPORT_TEAM", "SERVICE_HEAD", "IT_ADMIN", "COMPANY_ADMIN"];

  useEffect(() => {
    api.get("/ticket-config")
      .then((r) => setCfg({ allow_priority_choice: true, response_sla: {}, escalation: {}, ...r.data }))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  const priorityList = () => (Array.isArray(cfg.priorities) ? cfg.priorities : String(cfg.priorities).split(",")).map((s) => s.trim()).filter(Boolean);

  const setCat = (i, patch) => setCfg((c) => ({ ...c, categories: c.categories.map((x, j) => j === i ? { ...x, ...patch } : x) }));
  const addCat = () => setCfg((c) => ({ ...c, categories: [...c.categories, { name: "", department: "General", issues: [] }] }));
  const rmCat = (i) => setCfg((c) => ({ ...c, categories: c.categories.filter((_, j) => j !== i) }));

  const setEsc = (p, levels) => setCfg((c) => ({ ...c, escalation: { ...c.escalation, [p]: levels } }));
  const addLevel = (p) => {
    const cur = cfg.escalation?.[p] || [];
    setEsc(p, [...cur, { level: cur.length + 1, after_minutes: 30, notify_role: "SERVICE_HEAD" }]);
  };
  const setLevel = (p, i, patch) => setEsc(p, (cfg.escalation?.[p] || []).map((l, j) => j === i ? { ...l, ...patch } : l));
  const rmLevel = (p, i) => setEsc(p, (cfg.escalation?.[p] || []).filter((_, j) => j !== i).map((l, k) => ({ ...l, level: k + 1 })));

  const save = async () => {
    const categories = cfg.categories.filter((c) => c.name.trim()).map((c) => ({ name: c.name.trim(), department: c.department, issues: (Array.isArray(c.issues) ? c.issues : String(c.issues).split("\n")).map((s) => s.trim()).filter(Boolean) }));
    const priorities = priorityList();
    const sla = {}, response_sla = {}, escalation = {};
    priorities.forEach((p) => {
      sla[p] = Number(cfg.sla?.[p]) || 120;
      response_sla[p] = Number(cfg.response_sla?.[p]) || 30;
      escalation[p] = (cfg.escalation?.[p] || []).map((l, i) => ({ level: i + 1, after_minutes: Number(l.after_minutes) || 0, notify_role: l.notify_role || "SERVICE_HEAD" }));
    });
    setSaving(true);
    try {
      const { data } = await api.put("/ticket-settings", { categories, priorities, sla, response_sla, escalation, allow_priority_choice: cfg.allow_priority_choice });
      setCfg({ allow_priority_choice: true, response_sla: {}, escalation: {}, ...data });
      toast.success("Configuration saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!cfg) return <Loader />;
  const priorities = priorityList();

  return (
    <div>
      <PageHeader title="Ticket Configuration" subtitle="Categories, priorities, SLAs and the escalation matrix."
        actions={<Button onClick={save} disabled={saving} data-testid="save-ticketcfg-btn" className="bg-navy-900"><Save size={15} className="mr-1.5" /> {saving ? "Saving…" : "Save Changes"}</Button>} />

      <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-navy-900">Categories → Department → Issues</h3>
          <Button size="sm" variant="outline" onClick={addCat} data-testid="add-category-btn"><Plus size={14} className="mr-1" /> Add Category</Button>
        </div>
        <div className="space-y-4">
          {cfg.categories.map((c, i) => (
            <div key={i} className="grid md:grid-cols-[1fr_150px_2fr_auto] gap-3 items-start border-b border-slate-100 pb-4" data-testid={`category-row-${i}`}>
              <div><Label className="text-xs">Category</Label><Input className="mt-1 h-9" value={c.name} data-testid={`cat-name-${i}`} onChange={(e) => setCat(i, { name: e.target.value })} /></div>
              <div><Label className="text-xs">Department</Label>
                <Select value={c.department} onValueChange={(v) => setCat(i, { department: v })}>
                  <SelectTrigger className="mt-1 h-9" data-testid={`cat-dept-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Issues (one per line)</Label><Textarea className="mt-1" rows={3} data-testid={`cat-issues-${i}`} value={Array.isArray(c.issues) ? c.issues.join("\n") : c.issues} onChange={(e) => setCat(i, { issues: e.target.value.split("\n") })} /></div>
              <Button size="sm" variant="outline" onClick={() => rmCat(i)} className="mt-6 border-rose-200 text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></Button>
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mb-5">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-display font-semibold text-navy-900 mb-3">Priorities</h3>
          <Label className="text-xs">Comma-separated</Label>
          <Input className="mt-1" data-testid="priorities-input" value={Array.isArray(cfg.priorities) ? cfg.priorities.join(", ") : cfg.priorities} onChange={(e) => setCfg({ ...cfg, priorities: e.target.value.split(",") })} />
          <label className="flex items-center gap-2 text-sm text-slate-600 mt-3 cursor-pointer">
            <input type="checkbox" checked={!!cfg.allow_priority_choice} data-testid="allow-priority" onChange={(e) => setCfg({ ...cfg, allow_priority_choice: e.target.checked })} /> Let requesters choose priority
          </label>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-display font-semibold text-navy-900 mb-1">Response &amp; Resolution SLA</h3>
          <p className="text-xs text-slate-400 mb-3">Minutes per priority. Response = first acknowledgement, Resolution = time to close.</p>
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-2 items-center">
            <span className="text-xs font-semibold text-slate-400"></span>
            <span className="text-xs font-semibold text-slate-400 text-center w-28">Response (min)</span>
            <span className="text-xs font-semibold text-slate-400 text-center w-28">Resolution (min)</span>
            {priorities.map((p) => (
              <React.Fragment key={p}>
                <span className="text-sm text-navy-900">{p}</span>
                <Input type="number" className="h-9 w-28" data-testid={`response-sla-${p}`} value={cfg.response_sla?.[p] ?? ""} onChange={(e) => setCfg({ ...cfg, response_sla: { ...cfg.response_sla, [p]: e.target.value } })} />
                <Input type="number" className="h-9 w-28" data-testid={`sla-${p}`} value={cfg.sla?.[p] ?? ""} onChange={(e) => setCfg({ ...cfg, sla: { ...cfg.sla, [p]: e.target.value } })} />
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-1"><ArrowUpRight size={18} className="text-navy-900" /><h3 className="font-display font-semibold text-navy-900">Escalation Matrix</h3></div>
        <p className="text-xs text-slate-400 mb-4">For each priority, define who gets notified when a ticket stays unresolved past a time threshold (minutes from creation).</p>
        <div className="space-y-5">
          {priorities.map((p) => (
            <div key={p} className="border border-slate-100 rounded-xl p-4" data-testid={`escalation-${p}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold text-navy-900">{p}</span>
                <Button size="sm" variant="outline" onClick={() => addLevel(p)} data-testid={`add-escalation-level-${p}`}><Plus size={14} className="mr-1" /> Add Level</Button>
              </div>
              {(cfg.escalation?.[p] || []).length === 0 ? <p className="text-sm text-slate-400">No escalation levels. Add one to auto-notify seniors.</p> : (
                <div className="space-y-2">
                  {(cfg.escalation?.[p] || []).map((l, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-3" data-testid={`escalation-level-${p}-${i}`}>
                      <span className="text-xs font-bold text-white bg-navy-900 rounded-full w-6 h-6 flex items-center justify-center">{i + 1}</span>
                      <span className="text-sm text-slate-500">after</span>
                      <Input type="number" className="h-9 w-24" data-testid={`escalation-mins-${p}-${i}`} value={l.after_minutes} onChange={(e) => setLevel(p, i, { after_minutes: e.target.value })} />
                      <span className="text-sm text-slate-500">min → notify</span>
                      <Select value={l.notify_role} onValueChange={(v) => setLevel(p, i, { notify_role: v })}>
                        <SelectTrigger className="h-9 w-44" data-testid={`escalation-role-${p}-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button size="sm" variant="outline" onClick={() => rmLevel(p, i)} className="border-rose-200 text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
